import React, { useRef, useEffect, useState, useCallback } from "react";
import { FaListUl } from "react-icons/fa6";
import { FaShareAlt, FaShopify, FaWordpress, FaMagento, FaSalesforce } from "react-icons/fa";
import { IoLayers } from "react-icons/io5";
import { MdEmail } from "react-icons/md";

const DEFAULT_FILTERS = {
  status: [],
  taskType: null,
  sortBy: 'created_at',
  sortOrder: 'desc',
};

const STATUS_OPTIONS = [
  { value: 'running', label: 'Running', color: 'amber' },
  { value: 'pending', label: 'Queued', color: 'orange' },
  { value: 'completed', label: 'Completed', color: 'emerald' },
  { value: 'failed', label: 'Failed', color: 'rose' },
  { value: 'stopped', label: 'Stopped', color: 'gray' },
];

// Type chips. `active` holds the full class strings literally so Tailwind keeps them; the
// catalogue platforms reuse an existing colour scale for the same reason (and because orange,
// purple and sky happen to be Magento's, WooCommerce's and Salesforce's brand colours).
const TYPE_OPTIONS = [
  { value: 'extract_list', label: 'List', Icon: FaListUl, active: 'bg-blue-100 dark:bg-blue-950/30 border-blue-300 dark:border-blue-500/30 text-blue-700 dark:text-blue-300' },
  { value: 'extract_page_details', label: 'Page details', Icon: IoLayers, active: 'bg-purple-100 dark:bg-purple-950/30 border-purple-300 dark:border-purple-500/30 text-purple-700 dark:text-purple-300' },
  { value: 'extract_pages', label: 'Page Extractor 2.0', Icon: IoLayers, active: 'bg-indigo-100 dark:bg-indigo-950/30 border-indigo-300 dark:border-indigo-500/30 text-indigo-700 dark:text-indigo-300' },
  { value: 'extract_emails', label: 'Emails', Icon: MdEmail, active: 'bg-emerald-100 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300' },
  { value: 'extract_social_links', label: 'Social links', Icon: FaShareAlt, active: 'bg-orange-100 dark:bg-orange-950/30 border-orange-300 dark:border-orange-500/30 text-orange-700 dark:text-orange-300' },
  { value: 'extract_shopify', label: 'Shopify', Icon: FaShopify, active: 'bg-shopify-100 dark:bg-shopify-900/30 border-shopify-300 dark:border-shopify-500/30 text-shopify-700 dark:text-shopify-300' },
  { value: 'extract_woocommerce', label: 'WooCommerce', Icon: FaWordpress, active: 'bg-purple-100 dark:bg-purple-950/30 border-purple-300 dark:border-purple-500/30 text-purple-700 dark:text-purple-300' },
  { value: 'extract_magento', label: 'Magento', Icon: FaMagento, active: 'bg-orange-100 dark:bg-orange-950/30 border-orange-300 dark:border-orange-500/30 text-orange-700 dark:text-orange-300' },
  { value: 'extract_salesforce_commerce', label: 'Salesforce Commerce', Icon: FaSalesforce, active: 'bg-sky-100 dark:bg-sky-950/30 border-sky-300 dark:border-sky-500/30 text-sky-700 dark:text-sky-300' },
];

const TYPE_CHIP_IDLE = 'bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06]';

const SORT_OPTIONS = [
  { value: 'created_at', label: 'Created' },
  { value: 'latest_activity', label: 'Activity' },
  { value: 'name', label: 'Name' },
];

const STATUS_COLORS = {
  amber: {
    active: 'bg-amber-100 dark:bg-amber-950/30 border-amber-300 dark:border-amber-500/30 text-amber-700 dark:text-amber-300',
    dot: 'bg-amber-500',
  },
  orange: {
    active: 'bg-orange-100 dark:bg-orange-950/30 border-orange-300 dark:border-orange-500/30 text-orange-700 dark:text-orange-300',
    dot: 'bg-orange-400',
  },
  emerald: {
    active: 'bg-emerald-100 dark:bg-emerald-950/30 border-emerald-300 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-300',
    dot: 'bg-emerald-500',
  },
  rose: {
    active: 'bg-rose-100 dark:bg-rose-950/30 border-rose-300 dark:border-rose-500/30 text-rose-700 dark:text-rose-300',
    dot: 'bg-rose-500',
  },
  gray: {
    active: 'bg-gray-200 dark:bg-gray-700/30 border-gray-400 dark:border-gray-500/30 text-gray-700 dark:text-gray-300',
    dot: 'bg-gray-400',
  },
};

const FilterPanel = ({ filters, onChange, onClose, anchorRef }) => {
  const panelRef = useRef(null);
  const [position, setPosition] = useState(null);

  // Calculate position relative to the anchor element with smart positioning
  useEffect(() => {
    if (anchorRef?.current && panelRef.current) {
      const anchorRect = anchorRef.current.getBoundingClientRect();
      const panelRect = panelRef.current.getBoundingClientRect();

      const spaceBelow = window.innerHeight - anchorRect.bottom;
      const spaceAbove = anchorRect.top;
      const panelHeight = panelRect.height;

      let top;
      if (spaceBelow < panelHeight + 16 && spaceAbove > spaceBelow) {
        top = anchorRect.top - panelHeight - 8;
      } else {
        top = anchorRect.bottom + 8;
      }

      let left = anchorRect.right - panelRect.width;

      // Ensure it doesn't go off-screen horizontally
      if (left < 16) left = 16;
      left = Math.min(left, window.innerWidth - panelRect.width - 16);

      setPosition({ top, left });
    }
  }, [anchorRef]);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const toggleStatus = useCallback((value) => {
    const current = filters.status || [];
    const next = current.includes(value)
      ? current.filter(s => s !== value)
      : [...current, value];
    onChange({ ...filters, status: next });
  }, [filters, onChange]);

  const setTaskType = useCallback((value) => {
    onChange({ ...filters, taskType: filters.taskType === value ? null : value });
  }, [filters, onChange]);

  const setSortBy = useCallback((value) => {
    onChange({ ...filters, sortBy: value });
  }, [filters, onChange]);

  const toggleSortOrder = useCallback(() => {
    onChange({ ...filters, sortOrder: filters.sortOrder === 'desc' ? 'asc' : 'desc' });
  }, [filters, onChange]);

  const isDefault = (
    (filters.status?.length || 0) === 0 &&
    !filters.taskType &&
    filters.sortBy === 'created_at' &&
    filters.sortOrder === 'desc'
  );

  const resetAll = useCallback(() => {
    onChange({ ...DEFAULT_FILTERS });
  }, [onChange]);

  const panelContent = (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2">
        <span className="text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider">
          Filters
        </span>
        {!isDefault && (
          <button
            onClick={resetAll}
            className="text-[11px] font-medium text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
          >
            Reset all
          </button>
        )}
      </div>

      <div className="px-4 pb-3 space-y-3">
        {/* Status */}
        <div>
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Status</div>
          <div className="flex flex-wrap gap-1.5">
            {STATUS_OPTIONS.map(opt => {
              const isActive = filters.status?.includes(opt.value);
              const colors = STATUS_COLORS[opt.color];
              return (
                <button
                  key={opt.value}
                  onClick={() => toggleStatus(opt.value)}
                  className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all duration-150
                    ${isActive
                      ? colors.active
                      : 'bg-gray-50 dark:bg-white/[0.03] border-gray-200 dark:border-white/[0.06] text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-white/[0.06]'
                    }`}
                >
                  <div className={`w-1.5 h-1.5 rounded-full ${isActive ? colors.dot : 'bg-gray-300 dark:bg-gray-600'}`} />
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Automation type */}
        <div>
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Type</div>
          <div className="flex flex-wrap gap-1.5">
            {TYPE_OPTIONS.map(({ value, label, Icon, active }) => (
              <button
                key={value}
                onClick={() => setTaskType(value)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-all duration-150
                  ${filters.taskType === value ? active : TYPE_CHIP_IDLE}`}
              >
                <Icon className="w-2.5 h-2.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Sort */}
        <div>
          <div className="text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1.5">Sort by</div>
          <div className="flex items-center gap-1.5">
            <div className="inline-flex items-center gap-0.5 bg-gray-100 dark:bg-white/[0.03] rounded-full p-0.5 border border-gray-200 dark:border-white/[0.06]">
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-all duration-200
                    ${filters.sortBy === opt.value
                      ? 'bg-white dark:bg-white/[0.08] text-gray-800 dark:text-gray-100 shadow-sm border border-gray-200 dark:border-white/[0.1]'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 border border-transparent'
                    }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {/* Sort direction toggle */}
            <button
              onClick={toggleSortOrder}
              className="p-1.5 rounded-full bg-gray-100 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-white/[0.06] transition-all duration-150"
              title={filters.sortOrder === 'desc' ? 'Descending' : 'Ascending'}
            >
              <svg
                className={`w-3 h-3 transition-transform duration-200 ${filters.sortOrder === 'asc' ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Invisible backdrop to catch clicks outside */}
      <div className="fixed inset-0 z-[60]" onClick={onClose} style={{ pointerEvents: 'none' }}>
        <div className="absolute inset-0" style={{ pointerEvents: 'auto' }} onClick={onClose} />
      </div>

      {/* Panel - only show when positioned to avoid animation glitch */}
      {position && (
        <div
          ref={panelRef}
          className="fixed z-[70] w-72 rounded-xl shadow-2xl border border-gray-200 dark:border-white/[0.08] bg-white dark:bg-gray-900 animate-in fade-in duration-150"
          style={{ top: `${position.top}px`, left: `${position.left}px` }}
        >
          {panelContent}
        </div>
      )}

      {/* Hidden ref for initial measurement */}
      {!position && (
        <div
          ref={panelRef}
          className="fixed z-[70] w-72 opacity-0 pointer-events-none"
          style={{ top: 0, left: 0 }}
        >
          {panelContent}
        </div>
      )}
    </>
  );
};

export { DEFAULT_FILTERS };
export default FilterPanel;
